import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import PageHero from "../../common/ui/PageHero";
import { LoadingBlock, EmptyBlock, ErrorBlock } from "../../common/ui/StateBlock";
import { formatPrice, formatUsdFromGel, carTitle, coverImage, formatDateTime, localizeEventTitle } from "../../common/format";
import { currentLanguage } from "../../common/ui/notify";
import { t } from "./auctionI18n";
import useWatchlist from "../../common/useWatchlist";
import WatchButton from "./WatchButton";
import "./Auction.css";
import "../../common/ui/ui.css";

const lotStatusLabel = (status) => ({
    pending: t("მოლოდინში", "Pending", "В ожидании", "Սպասման մեջ"),
    open: t("წინასწარი ბიდი", "Pre-bid", "Предварительная ставка", "Նախնական հայտ"),
    live: t("ლაივ", "Live", "В эфире", "Ուղիղ"),
    closed: t("დახურული", "Closed", "Закрыт", "Փակված"),
    sold: t("გაყიდული", "Sold", "Продано", "Վաճառված"),
    withdrawn: t("მოხსნილი", "Withdrawn", "Снят", "Հանված"),
}[status] || status);

const AuctionEvent = () => {
    const lang = currentLanguage();
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [state, setState] = useState("loading");
    const watch = useWatchlist();

    const load = useCallback(() => {
        setState("loading");
        axios.get(`/api/auction/events/${id}`)
            .then((res) => { setData(res.data); setState("ready"); })
            .catch(() => setState("error"));
    }, [id]);
    useEffect(load, [load]);

    const event = data && data.event;
    const usdRate = event && event.usd_rate ? Number(event.usd_rate) : null;
    const lots = (data && data.lots) || [];

    return (
        <>
            <PageHero eyebrow={t("აუქციონი", "Auction", "Аукцион", "Աճուրդ")}
                title={event ? (localizeEventTitle(event.title, lang) || t(`აუქციონი #${event.id}`, `Auction #${event.id}`, `Аукцион #${event.id}`, `Աճուրդ #${event.id}`)) : "..."}
                subtitle={event ? formatDateTime(event.starts_at) : ""} />
            <div className="container auction-list">
                <Link to="/auctions" className="auction-back">← {t("ყველა აუქციონი", "All auctions", "Все аукционы", "Բոլոր աճուրդները")}</Link>
                {event && ["scheduled", "live", "paused"].includes(event.status) && (
                    <Link to={`/auction/event/${event.id}/live`} className="cx-btn cx-btn-primary auction-live-cta">
                        {event.status === "live" ? t("🔴 ლაივ აუქციონში შესვლა", "🔴 Enter live auction", "🔴 Войти в живой аукцион", "🔴 Մտնել կենդանի աճուրդ")
                            : event.status === "paused" ? t("ლაივ აუქციონში შესვლა (პაუზა)", "Enter live auction (paused)", "Войти в живой аукцион (пауза)", "Մտնել կենդանի աճուրդ (դադար)")
                            : t("აუქციონის ნახვა (მალე დაიწყება)", "View auction (starting soon)", "Смотреть аукцион (скоро начнётся)", "Դիտել աճուրդը (շուտով)")}
                    </Link>
                )}
                {state === "loading" && <LoadingBlock label={t("იტვირთება...", "Loading...", "Загрузка...", "Բեռնվում է...")} />}
                {state === "error" && <ErrorBlock title={t("შეცდომა", "Error", "Ошибка", "Սխալ")} message={t("ჩატვირთვა ვერ მოხერხდა.", "Could not load.", "Не удалось загрузить.", "Չհաջողվեց բեռնել։")} retryLabel={t("თავიდან", "Retry", "Повторить", "Կրկին")} onRetry={load} />}
                {state === "ready" && lots.length === 0 && <EmptyBlock title={t("ლოტები არ არის", "No lots", "Нет лотов", "Լոտեր չկան")} message={t("ამ აუქციონში ჯერ ლოტი არ დამატებულა.", "No lots have been added to this auction yet.", "В этот аукцион ещё не добавлены лоты.", "Այս աճուրդին դեռ լոտ չի ավելացվել։")} />}
                {state === "ready" && lots.length > 0 && (
                    <div className="auction-grid">
                        {lots.map((lot) => {
                            const cover = coverImage(lot);
                            return (
                                <Link to={`/auction/lot/${lot.id}`} key={lot.id} className="cx-card auction-lot-card">
                                    <div className="auction-lot-thumb" style={cover ? { backgroundImage: `url('${cover}')` } : {}}>
                                        <span className={`auction-chip auction-chip-${lot.status}`}>{lotStatusLabel(lot.status)}</span>
                                        <div className="auction-lot-watch">
                                            <WatchButton compact watched={watch.has(lot.car_id)} onToggle={() => watch.toggle(lot.car_id)} />
                                        </div>
                                    </div>
                                    <div className="auction-lot-body">
                                        <h3>{carTitle(lot)}</h3>
                                        <div className="auction-lot-price">
                                            <span className="auction-muted">{t("მიმდინარე", "Current", "Текущая", "Ընթացիկ")}</span>
                                            <span className="auction-price-stack">
                                                <strong>{formatPrice(lot.current_price || lot.start_price, "GEL")}</strong>
                                                {usdRate && formatUsdFromGel(lot.current_price || lot.start_price, usdRate)
                                                    ? <span className="auction-usd-sub">{formatUsdFromGel(lot.current_price || lot.start_price, usdRate)}</span> : null}
                                            </span>
                                        </div>
                                        {lot.has_reserve && (
                                            <span className={`auction-reserve ${lot.reserve_met ? "is-met" : "is-not"}`}>
                                                {lot.reserve_met
                                                    ? t("რეზერვი დაკმაყოფილდა", "reserve met", "резерв достигнут", "ռեզերվը բավարարված է")
                                                    : t("რეზერვი არ დაკმაყოფილდა", "reserve not met", "резерв не достигнут", "ռեզերվը չի բավարարվել")}
                                            </span>
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default AuctionEvent;
